import { Link } from 'react-router';
import { assetUrl } from '../lib/api';
import { useMyProfile } from '../lib/queries';
import { initials, type Session } from '../lib/session';

/**
 * The disc that is the way to the profile from either header. Their own photograph once they have
 * added one (PRF-02), their initials until then.
 *
 * It reads the profile rather than the session because that is where the photograph lives, and
 * because the picker seeds that same cache: the disc changes in the header the moment an upload
 * finishes, without a second request and without waiting for the session to go stale.
 *
 * The word under it is the same one the tab bar uses, for the same reason the tab bar has one: a
 * disc with a face in it says "you", not "your profile is through here", and on the home screen —
 * the one screen of the phone app with no tab bar — this is the only way to it.
 *
 * One component for two bars, so the phone's header and the laptop's cannot drift apart. What
 * differs is passed in — the ring the desktop header wears while the profile is the open screen,
 * and the nudge that lines the phone's up with the lockup beside it.
 */
export function AccountLink({
  session,
  current = false,
  className = '',
}: {
  session: Session;
  current?: boolean;
  className?: string;
}) {
  const profile = useMyProfile();
  const photo = assetUrl(profile.data?.photoAssetId);

  return (
    <Link
      to="/account"
      // Their name is worth saying, but the visible word comes first: a control that reads
      // "Profile" has to answer to it.
      aria-label={`Profile: ${session.firstName} ${session.lastName}`}
      aria-current={current ? 'page' : undefined}
      className={`flex shrink-0 flex-col items-center gap-1 ${className}`}
    >
      <span
        className={`text-chip flex size-tap items-center justify-center overflow-hidden rounded-full bg-sand-3 font-semibold text-ink-2 ${
          current ? 'ring-2 ring-ink ring-offset-2 ring-offset-surface' : ''
        }`}
      >
        {photo ? (
          <img
            src={photo}
            alt=""
            // Stored square already, so `cover` only has to survive a browser rounding the box.
            className="size-full object-cover"
            decoding="async"
          />
        ) : (
          initials(session)
        )}
      </span>
      <span
        className={`text-chip ${current ? 'font-semibold text-ink' : 'font-medium text-ink-4'}`}
      >
        Profile
      </span>
    </Link>
  );
}
